// Vetproof Scan — the vet's mobile flow: scan (camera QR or RFID reader) → known pet or new patient.
// Full-screen on a phone; shown inside a phone frame on larger screens.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { EmergencyCard, Me, RegisterResult } from "../../../shared/types";
import { api, ApiError } from "../../api";
import { navigate } from "../../router";
import { conditionName, speciesEmoji } from "../../format";
import { QrScanner } from "./QrScanner";
import { RfidScanner } from "./RfidScanner";
import { RegisterPet } from "./RegisterPet";
import { chipOrigin, formatChip, parseChipPayload, scanFeedback } from "./chip";

type Mode = "qr" | "rfid" | "manual";
type Stage =
  | { k: "scan" }
  | { k: "lookup"; chip: string }
  | { k: "found"; chip: string; card: EmergencyCard }
  | { k: "new"; chip: string }
  | { k: "register"; chip: string }
  | { k: "registered"; chip: string; result: RegisterResult; name: string }
  | { k: "error"; chip: string; message: string };

const VIA: Record<Mode, string> = { qr: "QR code", rfid: "RFID reader", manual: "manual entry" };

export function ScanApp({ me, initialChip }: { me: Me; initialChip?: string }) {
  const [mode, setMode] = useState<Mode>("qr");
  const [stage, setStage] = useState<Stage>({ k: "scan" });
  const [via, setVia] = useState<string>("QR code");

  const lookup = useCallback(async (chip: string, source: string) => {
    scanFeedback();
    setVia(source);
    setStage({ k: "lookup", chip });
    try {
      setStage({ k: "found", chip, card: await api.emergency(chip) });
    } catch (e) {
      if ((e as ApiError).status === 404) setStage({ k: "new", chip });
      else setStage({ k: "error", chip, message: (e as Error).message });
    }
  }, []);

  // Opened from the owner's QR with the phone's own camera app: /scan?chip=…
  useEffect(() => {
    const chip = initialChip && parseChipPayload(initialChip);
    if (chip) lookup(chip, "QR code");
  }, [initialChip, lookup]);

  const onQr = useCallback((chip: string) => lookup(chip, VIA.qr), [lookup]);
  const onRfid = useCallback((chip: string) => lookup(chip, VIA.rfid), [lookup]);
  const reset = () => { setStage({ k: "scan" }); navigate("/scan", true); };

  if (me.role !== "vet") {
    return <main className="container"><div className="err">Scanning is available to clinic staff only.</div></main>;
  }

  return (
    <div className="phone-stage">
      <aside className="phone-aside">
        <h2>Vetproof Scan</h2>
        <p>The clinic's mobile app. Identify a pet in seconds, either way:</p>
        <ul>
          <li><b>📷 QR code</b> — the owner shows the pet's Vetproof QR (in the owner app, <i>Owner &amp; ID</i> tab). Uses this device's real camera.</li>
          <li><b>📡 RFID</b> — a handheld 134.2 kHz microchip reader paired over Bluetooth (simulated here).</li>
        </ul>
        <p>A known chip opens the verified record. An unknown chip starts the new-patient wizard and seals the registration as block #0.</p>
        <p className="fine">On a phone, open <code>{location.host}/scan</code> — the app runs full-screen and can be added to the home screen.</p>
      </aside>

      <div className="phone">
        <div className="app">
          <header className="app-h">
            <button className="app-x" onClick={() => navigate("/")} aria-label="Close">‹</button>
            <div>
              <div className="app-t">Vetproof Scan</div>
              <div className="app-s">{me.vet?.clinicName}</div>
            </div>
            <img src="/favicon.png" alt="" />
          </header>

          <div className="app-body">
            {stage.k === "scan" && (
              <>
                {mode === "qr" && <QrScanner onChip={onQr} />}
                {mode === "rfid" && <RfidScanner onChip={onRfid} />}
                {mode === "manual" && <ManualEntry onChip={(c) => lookup(c, VIA.manual)} />}
              </>
            )}

            {stage.k === "lookup" && (
              <div className="result center-col"><div className="spinner" />Looking up chip<br /><code>{formatChip(stage.chip)}</code></div>
            )}

            {stage.k === "found" && <Found card={stage.card} via={via} onAgain={reset} />}

            {stage.k === "new" && (
              <div className="result">
                <div className="res-badge new">New chip</div>
                <div className="big-ico">🆕</div>
                <h3>Not registered on Vetproof</h3>
                <p className="t2">Read via {via}. No pet on the network carries this microchip.</p>
                <div className="chip-box"><code>{formatChip(stage.chip)}</code><span>{chipOrigin(stage.chip)}</span></div>
                <button className="btn teal big" onClick={() => setStage({ k: "register", chip: stage.chip })}>Register new patient</button>
                <button className="btn ghost big" onClick={reset}>Scan again</button>
              </div>
            )}

            {stage.k === "register" && (
              <RegisterPet chip={stage.chip} onCancel={() => setStage({ k: "new", chip: stage.chip })}
                onDone={(result, name) => setStage({ k: "registered", chip: stage.chip, result, name })} />
            )}

            {stage.k === "registered" && (
              <div className="result">
                <div className="res-badge ok">🔗 Sealed · block #{stage.result.seq}</div>
                <div className="big-ico">✅</div>
                <h3>{stage.name} is registered</h3>
                <p className="t2">Patient ID {stage.result.patient_no} · microchip {formatChip(stage.chip)}</p>
                <div className="chip-box"><span>Genesis hash</span><code className="hash">{stage.result.hash}</code></div>
                <button className="btn teal big" onClick={() => navigate(`/pets/${stage.result.pet_id}`)}>Open record &amp; add first visit</button>
                <button className="btn ghost big" onClick={reset}>Scan next pet</button>
              </div>
            )}

            {stage.k === "error" && (
              <div className="result">
                <div className="err">{stage.message}</div>
                <button className="btn ghost big" onClick={reset}>Try again</button>
              </div>
            )}
          </div>

          {stage.k === "scan" && (
            <nav className="app-tabs">
              <button className={mode === "qr" ? "on" : ""} onClick={() => setMode("qr")}><span>📷</span>QR code</button>
              <button className={mode === "rfid" ? "on" : ""} onClick={() => setMode("rfid")}><span>📡</span>RFID reader</button>
              <button className={mode === "manual" ? "on" : ""} onClick={() => setMode("manual")}><span>⌨️</span>Type</button>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
}

function Found({ card, via, onAgain }: { card: EmergencyCard; via: string; onAgain: () => void }) {
  const { pet, allergies } = card;
  return (
    <div className="result found">
      <div className={`res-badge ${card.verified ? "ok" : "bad"}`}>{card.verified ? "🔗 Verified record" : "⚠ Chain verification failed"}</div>
      <div className="found-head">
        {pet.photoUrl ? <img className="avatar lg" src={pet.photoUrl} alt="" /> : <div className="avatar lg">{speciesEmoji(pet.species)}</div>}
        <div>
          <h3>{pet.displayName}</h3>
          <div className="t2">{[pet.species, pet.breed, pet.sex, pet.ageLabel].filter(Boolean).join(" · ")}</div>
          <div className="t3">ID {pet.patientNo} · read via {via}</div>
        </div>
      </div>
      <div className={`alert-box${allergies.drug.length ? " red" : ""}`}>
        <b>⛔ Drug allergies</b>
        {allergies.drug.length ? <div className="atags">{allergies.drug.map((a) => <span key={a} className="atag-red">{a}</span>)}</div> : <span className="t2"> None recorded</span>}
      </div>
      {card.conditions.length > 0 && (
        <div className="mini-list"><b>Active conditions</b>{card.conditions.slice(0, 3).map((c) => <div key={c.name}>{c.icon} {conditionName(c.name)} <span className="t3">· {c.status}</span></div>)}</div>
      )}
      {card.medications.length + card.externalMeds.length > 0 && (
        <div className="mini-list"><b>Current medications &amp; diet</b>
          {card.medications.slice(0, 3).map((m) => <div key={m.id}>{m.icon} {m.name}</div>)}
          {card.externalMeds.map((m) => <div key={m.name}>💊 {m.name} <span className="t3">· {m.source === "owner" ? "owner" : "other clinic"}</span></div>)}
        </div>
      )}
      <button className="btn teal big" onClick={() => navigate(`/pets/${pet.id}`)}>Open full record</button>
      <div className="row2">
        <button className="btn danger big" onClick={() => navigate(`/emergency/${pet.microchip}`)}>Emergency view</button>
        <button className="btn ghost big" onClick={onAgain}>Scan another</button>
      </div>
    </div>
  );
}

function ManualEntry({ onChip }: { onChip: (chip: string) => void }) {
  const [v, setV] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const chip = parseChipPayload(v);
    if (!chip) return setErr("Enter 9–15 digits");
    onChip(chip);
  };
  return (
    <form className="manual" onSubmit={submit}>
      <div className="big-ico">⌨️</div>
      <div className="field"><label>Microchip number</label>
        <input value={v} onChange={(e) => { setV(e.target.value); setErr(null); }} inputMode="numeric" autoFocus placeholder="941 000 029 159 723" />
      </div>
      {err && <div className="err">{err}</div>}
      <button className="btn teal big">Look up</button>
    </form>
  );
}
